import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** Shape of a stored entry in the in-memory cache */
interface CacheEntry {
  value: unknown;
  expiresAt?: number;
}

/** Callback type for pub/sub subscribers */
type SubscriberCallback = (msg: unknown) => void;

/**
 * Redis / Cache integration with in-memory default backend.
 *
 * When `REDIS_URL` is provided in `config.env`, a warning is logged and the
 * integration falls back to the in-memory store (real Redis client support is
 * not yet implemented). When `REDIS_URL` is absent the in-memory store is
 * used silently — this is the expected development mode.
 */
export class RedisIntegration extends BaseIntegration {
  private store: Map<string, CacheEntry> = new Map();
  private locks: Map<string, string> = new Map();
  private channels: Map<string, Set<SubscriberCallback>> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);

    const redisUrl = config.env.REDIS_URL;
    if (redisUrl) {
      this.logger.warn(
        'REDIS_URL is configured but real Redis client is not yet implemented. ' +
          'Falling back to in-memory store.',
        { redisUrl },
      );
    }

    this.logger.info('Redis integration initialized (in-memory backend)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'get':
          data = await this.executeWithRetry(() => this.getKey(params));
          break;
        case 'set':
          data = await this.executeWithRetry(() => this.setKey(params));
          break;
        case 'delete':
          data = await this.executeWithRetry(() => this.deleteKey(params));
          break;
        case 'lock':
          data = await this.executeWithRetry(() => this.acquireLock(params));
          break;
        case 'unlock':
          data = await this.executeWithRetry(() => this.releaseLock(params));
          break;
        case 'increment':
          data = await this.executeWithRetry(() => this.incrementKey(params));
          break;
        case 'expire':
          data = await this.executeWithRetry(() => this.expireKey(params));
          break;
        case 'publish':
          data = await this.executeWithRetry(() => this.publishMessage(params));
          break;
        case 'subscribe':
          data = await this.executeWithRetry(() =>
            this.subscribeChannel(params),
          );
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Remove expired entries on access and return whether a key is alive. */
  private isAlive(key: string): boolean {
    const entry = this.store.get(key);
    if (!entry) return false;
    if (entry.expiresAt !== undefined && Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return false;
    }
    return true;
  }

  /** Generate a unique lock identifier. */
  private generateLockId(): string {
    return `lock_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async getKey(
    params: IntegrationParams,
  ): Promise<{ value: unknown }> {
    const key = params.key as string;

    this.logger.debug('Cache GET', { key });

    if (!this.isAlive(key)) {
      return { value: null };
    }

    const entry = this.store.get(key);
    return { value: entry ? entry.value : null };
  }

  private async setKey(
    params: IntegrationParams,
  ): Promise<{ ok: boolean }> {
    const key = params.key as string;
    const value = params.value;
    const ttl = params.ttl as number | undefined;

    this.logger.debug('Cache SET', { key, ttl });

    const entry: CacheEntry = { value };
    if (ttl !== undefined && ttl > 0) {
      entry.expiresAt = Date.now() + ttl * 1000;
    }

    this.store.set(key, entry);
    return { ok: true };
  }

  private async deleteKey(
    params: IntegrationParams,
  ): Promise<{ deleted: boolean }> {
    const key = params.key as string;

    this.logger.debug('Cache DELETE', { key });

    const existed = this.store.has(key);
    this.store.delete(key);
    return { deleted: existed };
  }

  private async acquireLock(
    params: IntegrationParams,
  ): Promise<{ acquired: boolean; lockId: string }> {
    const key = params.key as string;
    const ttl = (params.ttl as number | undefined) ?? 30_000;

    this.logger.debug('Cache LOCK', { key, ttl });

    // Check if the lock already exists and is still alive
    const existingLockId = this.locks.get(key);
    if (existingLockId) {
      // The lock key in the store tracks its expiry
      if (this.isAlive(`__lock:${key}`)) {
        return { acquired: false, lockId: '' };
      }
      // Expired — clean up
      this.locks.delete(key);
      this.store.delete(`__lock:${key}`);
    }

    const lockId = this.generateLockId();
    this.locks.set(key, lockId);
    // Store the lock with a TTL (ttl param is in ms, expiresAt is epoch ms)
    this.store.set(`__lock:${key}`, {
      value: lockId,
      expiresAt: Date.now() + ttl,
    });

    return { acquired: true, lockId };
  }

  private async releaseLock(
    params: IntegrationParams,
  ): Promise<{ released: boolean }> {
    const key = params.key as string;
    const lockId = params.lockId as string;

    this.logger.debug('Cache UNLOCK', { key, lockId });

    const currentLockId = this.locks.get(key);
    if (currentLockId !== lockId) {
      return { released: false };
    }

    this.locks.delete(key);
    this.store.delete(`__lock:${key}`);
    return { released: true };
  }

  private async incrementKey(
    params: IntegrationParams,
  ): Promise<{ value: number }> {
    const key = params.key as string;
    const by = (params.by as number | undefined) ?? 1;

    this.logger.debug('Cache INCR', { key, by });

    let current = 0;

    if (this.isAlive(key)) {
      const entry = this.store.get(key);
      if (entry) {
        current = typeof entry.value === 'number' ? entry.value : 0;
      }
    }

    const newValue = current + by;
    // Preserve existing TTL if the key already exists
    const existing = this.store.get(key);
    this.store.set(key, {
      value: newValue,
      expiresAt: existing?.expiresAt,
    });

    return { value: newValue };
  }

  private async expireKey(
    params: IntegrationParams,
  ): Promise<{ set: boolean }> {
    const key = params.key as string;
    const ttl = params.ttl as number;

    this.logger.debug('Cache EXPIRE', { key, ttl });

    if (!this.isAlive(key)) {
      return { set: false };
    }

    const entry = this.store.get(key);
    if (!entry) {
      return { set: false };
    }

    entry.expiresAt = Date.now() + ttl * 1000;
    this.store.set(key, entry);
    return { set: true };
  }

  private async publishMessage(
    params: IntegrationParams,
  ): Promise<{ receivers: number }> {
    const channel = params.channel as string;
    const message = params.message;

    this.logger.debug('Cache PUBLISH', { channel });

    const subscribers = this.channels.get(channel);
    if (!subscribers || subscribers.size === 0) {
      return { receivers: 0 };
    }

    for (const callback of subscribers) {
      try {
        callback(message);
      } catch (err) {
        this.logger.error('Subscriber callback error', {
          channel,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    return { receivers: subscribers.size };
  }

  private async subscribeChannel(
    params: IntegrationParams,
  ): Promise<{ subscribed: boolean }> {
    const channel = params.channel as string;

    this.logger.debug('Cache SUBSCRIBE', { channel });

    if (!this.channels.has(channel)) {
      this.channels.set(channel, new Set());
    }

    // The in-memory subscribe registers the channel but the actual callback
    // would be wired via a higher-level API.  For the integration contract we
    // simply confirm the subscription was created.
    return { subscribed: true };
  }
}

// Auto-register
registerIntegration('redis', RedisIntegration);
